import type { LinePlan } from '../../../shared/types';

/**
 * What to do once the memorised moves run out.
 *
 * This exists because the honest complaint about opening trainers is that they
 * stop exactly where the hard part begins: you learn seven moves, the line ends
 * with "White's pawns are weak", and you are left holding a position you cannot
 * do anything with. Three columns, deliberately:
 *
 * - **yours** — concrete things to aim for, so there is a next move
 * - **theirs** — what the opponent wants, so it can be recognised and stopped
 * - **counters** — what to do when they try it
 *
 * The opponent's plan is given equal weight on purpose. Knowing only your own
 * ideas produces players who execute a plan into a refutation they never saw.
 */
export default function PlanPanel({ plan, side }: { plan: LinePlan; side: 'white' | 'black' }) {
  const them = side === 'white' ? 'Black' : 'White';

  return (
    <div className="plan">
      <h4 className="plan-head">The plan from here</h4>

      <div className="plan-cols">
        <section className="plan-col">
          <h5 className="plan-col-head plan-col-head--yours">What you are playing for</h5>
          <ul>
            {plan.yours.map((idea) => (
              <li key={idea}>{idea}</li>
            ))}
          </ul>
        </section>

        <section className="plan-col">
          <h5 className="plan-col-head plan-col-head--theirs">What {them} wants</h5>
          <ul>
            {plan.theirs.map((idea) => (
              <li key={idea}>{idea}</li>
            ))}
          </ul>
        </section>
      </div>

      {plan.counters && plan.counters.length > 0 && (
        <section className="plan-counters">
          <h5 className="plan-col-head">If they try it</h5>
          <dl>
            {plan.counters.map((c) => (
              <div className="plan-counter" key={c.when}>
                <dt>{c.when}</dt>
                <dd>{c.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
